/**
 * Lottie animations utilities for notifications
 * Uses the central lottie-registry for all animations
 */

import { getLottieSource, getAllLotties, getLottiesByCategory, LottieItem, LottieCategory } from "@/lib/lottie-registry";
import { LottieAnimationInfo } from "../types";

/**
 * Get Lottie animation source by ID
 * Falls back to Partying_Emoji if not found
 */
export const getLottieAnimation = (id: string | null | undefined): any => {
  if (!id) return getLottieSource("Partying_Emoji");
  return getLottieSource(id) || getLottieSource("Partying_Emoji");
};

/**
 * Get all animations as array for picker
 * Maps to the legacy LottieAnimationInfo format for backwards compatibility
 */
export const getLottieAnimationsList = (): LottieAnimationInfo[] => {
  return getAllLotties().map((item) => ({
    id: item.key,
    name: item.name,
    file: item.source,
    category: item.category as LottieAnimationInfo["category"],
  }));
};

/**
 * Get animations by category
 */
export const getLottieAnimationsByCategory = (
  category: LottieAnimationInfo["category"]
): LottieAnimationInfo[] => {
  // Map old "other" category to new categories
  const categoryMap: Record<string, LottieCategory[]> = {
    emoji: ["emoji"],
    celebration: ["celebration"],
    animal: ["animal"],
    other: ["action", "object", "people"],
  };

  const categories = categoryMap[category] || [category as LottieCategory];
  
  return categories.flatMap((cat) =>
    getLottiesByCategory(cat).map((item) => ({
      id: item.key,
      name: item.name,
      file: item.source,
      category: category,
    }))
  );
};

/**
 * Default animations for notification types
 */
export const DEFAULT_LOTTIE_BY_TYPE: Record<string, string> = {
  // Familie admin notification types
  score: "Partying_Emoji",
  score_positive: "Partying_Emoji",
  score_negative: "Loudly_Crying_Emoji",
  event: "calendar",
  birthday: "cake",
  challenge: "finger_crossed",
  custom: "confetti",
  // Super admin notification types
  role_assignment: "head_with_crown_emoji",
  new_weekend: "Partying_Emoji",
  new_feature: "cool",
  system_announcement: "Cool_Emoji",
  // Speurtocht notifications
  speurtocht_start: "party",
  speurtocht_step_complete: "confetti",
  speurtocht_complete: "confetti",
  // Game notifications
  game_challenge: "muscle_emoji",
  game_declined: "Loudly_Crying_Emoji",
  game_win: "confetti",
  game_loss: "Holding_Back_Tears",
};

/**
 * Legacy LOTTIE_ANIMATIONS object for backwards compatibility
 * @deprecated Use getLottieSource from @/lib/lottie-registry instead
 */
export const LOTTIE_ANIMATIONS: Record<string, LottieAnimationInfo> = Object.fromEntries(
  getAllLotties().map((item) => [
    item.key,
    {
      id: item.key,
      name: item.name,
      file: item.source,
      category: item.category as LottieAnimationInfo["category"],
    },
  ])
);
